/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TimeTicket, VersionVector } from '../yorkie';
import { Code, YorkieError } from './error';
import {
  DataSize,
  addDataSizes,
  subDataSize,
} from '@yorkie-js/sdk/src/util/resource';

/**
 * About `index`, `path`, `size` and `TreePos` in crdt.IndexTree.
 *
 * `index` of crdt.IndexTree represents a absolute position of a node in the tree.
 * `size` is used to calculate the relative index of nodes in the tree.
 * `index` in yorkie.IndexTree inspired by ProseMirror's index.
 *
 * For example, empty paragraph's size is 0 and index 0 is the position of the:
 *    0
 * <p> </p>,                                p.size = 0
 *
 * If a paragraph has <i>, its size becomes 2 and there are 3 indexes:
 *     0   1    2
 *  <p> <i> </i> </p>                       p.size = 2, i.size = 0
 *
 * If the paragraph has <i> and <b>, its size becomes 4:
 *     0   1    2   3   4
 *  <p> <i> </i> <b> </b> </p>              p.size = 4, i.size = 0, b.size = 0
 *     0   1    2   3    4    5   6
 *  <p> <i> </i> <b> </b> <s> </s> </p>     p.size = 6, i.size = 0, b.size = 0, s.size = 0
 *
 * If a paragraph has text, its size becomes length of the characters:
 *     0 1 2 3
 *  <p> A B C </p>                          p.size = 3,   text.size = 3
 *
 * So the size of a node is the sum of the size and type of its children:
 *  `size = children(element type).length * 2 + children.reduce((child, acc) => child.size + acc, 0)`
 *
 * `TreePos` is also used to represent the position in the tree. It contains node and offset.
 * `TreePos` can be converted to `index` and vice versa.
 *
 * For example, if a paragraph has <i>, there are 3 indexes:
 *     0   1    2
 *  <p> <i> </i> </p>                       p.size = 2, i.size = 0
 *
 * In this case, index of TreePos(p, 0) is 0, index of TreePos(p, 1) is 2.
 * Index 1 can be converted to TreePos(i, 0).
 *
 * `path` of crdt.IndexTree represents a position like `index` in crdt.IndexTree.
 * It contains offsets of each node from the root node as elements except the last.
 * The last element of the path represents the position in the parent node.
 *
 * Let's say we have a tree like this:
 *                     0 1 2
 * <p> <i> a b </i> <b> c d </b> </p>
 *
 * The path of the position between 'c' and 'd' is [1, 1]. The first element of the
 * path is the offset of the <b> in <p> and the second element represents the position
 * between 'c' and 'd' in <b>.
 */

/**
 * `ElementPaddingSize` is the size of an element node as a child of another element node.
 * Because an element node could be considered as a pair of open and close tags.
 */
export const ElementPaddingSize = 2;

/**
 * `DefaultRootType` is the default type of the root node.
 * It is used when the type of the root node is not specified.
 */
export const DefaultRootType = 'root';

/**
 * `DefaultTextType` is the default type of the text node.
 * It is used when the type of the text node is not specified.
 */
export const DefaultTextType = 'text';

/**
 * `NoteType` is the type of a node in the tree.
 */
export type TreeNodeType = string;

/**
 * `addSizeOfLeftSiblings` returns the size of left siblings of the given offset.
 * If includeRemoved is true, it includes removed nodes in the calculation.
 */
export function addSizeOfLeftSiblings<T extends IndexTreeNode<T>>(
  parent: T,
  offset: number,
  includeRemoved: boolean = false,
): number {
  let acc = 0;
  const siblings = includeRemoved ? parent.allChildren : parent.children;

  for (let i = 0; i < offset; i++) {
    const leftSibling = siblings[i];

    if (!leftSibling || (!includeRemoved && leftSibling.isRemoved)) {
      continue;
    }

    acc += leftSibling.paddedSize(includeRemoved);
  }

  return acc;
}

/**
 * `IndexTreeNode` is the node of IndexTree. It is used to represent the
 * document of text-based editors.
 */
export abstract class IndexTreeNode<T extends IndexTreeNode<T>> {
  type: TreeNodeType;
  parent?: T;
  _children: Array<T>;
  visibleSize: number;
  totalSize: number;

  constructor(type: TreeNodeType, children: Array<T> = []) {
    this.type = type;
    this.visibleSize = 0;
    this.totalSize = 0;
    this._children = children;

    if (this.isText && this._children.length > 0) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }
  }

  /**
   * `updateAncestorsSize` updates the size of the ancestors.
   * It is used when the size of the node is changed.
   * If includeRemoved is true, it updates ancestors totalSize including removed nodes.
   */
  updateAncestorsSize(delta: number, includeRemoved: boolean = false): void {
    let parent: T | undefined = this.parent;

    while (parent) {
      if (includeRemoved) {
        parent.totalSize += delta;
      } else {
        parent.visibleSize += delta;
        // NOTE(hackerwins): If a parent node is removed (tombstone),
        // it is not visible to the user, and its children are also not visible.
        // Therefore, there is no need to update the visibleSize above the removed parent.
        if (parent.isRemoved) {
          break;
        }
      }

      parent = parent.parent;
    }
  }

  /**
   * `UpdateDescendantsSize` updates the size of the descendants. It is used when
   * the tree is newly created and the size of the descendants is not calculated.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  updateDescendantsSize(includeRemoved: boolean = false): number {
    let size = 0;
    for (const child of this._children) {
      const childSize = child.updateDescendantsSize(includeRemoved);
      if (!includeRemoved && child.isRemoved) {
        continue;
      }

      size += childSize;
    }

    if (includeRemoved) {
      this.totalSize += size;
    } else {
      this.visibleSize += size;
    }

    return this.paddedSize(includeRemoved);
  }

  /**
   * `isText` returns true if the node is a text node.
   */
  get isText(): boolean {
    // TODO(hackerwins): We need to get the type of text node from user.
    // Consider the use schema to get the type of text node.
    return this.type === DefaultTextType;
  }

  /**
   * `paddedSize` returns the length of the node including padding.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  paddedSize(includeRemoved: boolean = false): number {
    let size = this.visibleSize;
    if (includeRemoved) {
      size = this.totalSize;
    }
    if (!this.isText) {
      size += ElementPaddingSize;
    }
    return size;
  }

  /**
   * `isAncenstorOf` returns true if the node is an ancestor of the given node.
   */
  isAncestorOf(node: T): boolean {
    return ancestorOf(this as any, node);
  }

  /**
   * `nextSibling` returns the next sibling of the node.
   */
  get nextSibling(): T | undefined {
    const offset = this.parent!.findOffset(this as any);
    const sibling = this.parent!.children[offset + 1];
    if (sibling) {
      return sibling;
    }

    return undefined;
  }

  /**
   * `prevSibling` returns the previous sibling of the node.
   */
  get prevSibling(): T | undefined {
    const offset = this.parent!.findOffset(this as any);
    const sibling = this.parent!.children[offset - 1];
    if (sibling) {
      return sibling;
    }

    return undefined;
  }

  /**
   * `isRemoved` returns true if the node is removed.
   */
  abstract get isRemoved(): boolean;

  /**
   * `cloneText` clones the text node with the given id and value.
   */
  abstract cloneText(offset: number): T;

  /**
   * `cloneElement` clones the element node with the given issueTimeTicket
   * function and value.
   */
  abstract cloneElement(issueTimeTicket: () => TimeTicket): T;

  /**
   * `value` returns the value of the node.
   */
  abstract get value();

  /**
   * `value` sets the value of the node.
   */
  abstract set value(v: string);

  /**
   * `getDataSize` returns the size of the node.
   */
  abstract getDataSize(): DataSize;

  /**
   * `splitText` splits the given node at the given offset.
   */
  splitText(offset: number, absOffset: number): [T | undefined, DataSize] {
    const diff = { data: 0, meta: 0 };

    if (offset === 0 || offset === this.visibleSize) {
      return [undefined, diff];
    }

    // A position that anchors past the end of this node cannot be resolved
    // here. `slice` would quietly hand back an empty right value and the split
    // would look like a no-op, leaving the edit at the wrong position.
    if (offset < 0 || offset > this.visibleSize) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `split at ${offset} of ${this.visibleSize}: offset out of range`,
      );
    }

    const leftValue = this.value.slice(0, offset);
    const rightValue = this.value.slice(offset);

    if (!rightValue.length) {
      return [undefined, diff];
    }

    const prvSize = this.getDataSize();

    this.value = leftValue;

    const rightNode = this.cloneText(offset + absOffset);
    rightNode.value = rightValue;

    this.parent!.insertAfterInternal(rightNode, this as any);

    // NOTE(hackerwins): Calculate data size after node splitting:
    // Take the sum of the two split nodes(left and right) minus the size of
    // the original node. This calculates the net metadata overhead added by
    // the split operation.
    addDataSizes(diff, this.getDataSize(), rightNode.getDataSize());
    subDataSize(diff, prvSize);

    return [rightNode, diff];
  }

  /**
   * `children` returns the children of the node.
   */
  get children(): Array<T> {
    // Tombstone nodes remain awhile in the tree during editing.
    // They will be removed after the editing is done.
    // So, we need to filter out the tombstone nodes to get the real children.
    return this._children.filter((child) => !child.isRemoved);
  }

  /**
   * `allChildren` returns all the children of the node including tombstone nodes.
   * It returns the shallow copy of the children.
   */
  get allChildren(): Array<T> {
    return [...this._children];
  }

  /**
   * `hasTextChild` returns true if the node's children consist of only text children.
   */
  hasTextChild(): boolean {
    return (
      this.children.length > 0 && this.children.every((child) => child.isText)
    );
  }

  /**
   * `getChildrenText` returns text value of all text type children.
   */
  getChildrenText(): string {
    if (this.isText) {
      return this.value;
    }
    if (this.hasTextChild()) {
      return this.children.map((child) => child.value).join('');
    }
    return '';
  }

  /**
   * `append` appends the given nodes to the children.
   */
  append(...newNode: Array<T>): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    this._children.push(...newNode);
    for (const node of newNode) {
      node.parent = this as any;
      node.updateAncestorsSize(node.paddedSize());
      node.updateAncestorsSize(node.paddedSize(true), true);
    }
  }

  /**
   * `prepend` prepends the given nodes to the children. It is only used
   * for creating a new node from snapshot.
   */
  prepend(...newNode: Array<T>): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    this._children.unshift(...newNode);
    for (const node of newNode) {
      node.parent = this as any;
    }
  }

  /**
   * `insertBefore` inserts the given node before the given child.
   */
  insertBefore(newNode: T, referenceNode: T): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const offset = this._children.indexOf(referenceNode);
    if (offset === -1) {
      throw new YorkieError(Code.ErrInvalidArgument, 'child not found');
    }

    this.insertAtInternal(newNode, offset);
    newNode.updateAncestorsSize(newNode.paddedSize());
    newNode.updateAncestorsSize(newNode.paddedSize(true), true);
  }

  /**
   * `insertAfter` inserts the given node after the given child.
   */
  insertAfter(newNode: T, referenceNode: T): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const offset = this._children.indexOf(referenceNode);
    if (offset === -1) {
      throw new YorkieError(Code.ErrInvalidArgument, 'child not found');
    }

    this.insertAtInternal(newNode, offset + 1);
    newNode.updateAncestorsSize(newNode.paddedSize());
    newNode.updateAncestorsSize(newNode.paddedSize(true), true);
  }

  /**
   * `insertAt` inserts the given node at the given offset.
   */
  insertAt(newNode: T, offset: number): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    this.insertAtInternal(newNode, offset);
    newNode.updateAncestorsSize(newNode.paddedSize());
    newNode.updateAncestorsSize(newNode.paddedSize(true), true);
  }

  /**
   * `removeChild` removes the given child.
   * In this method, the child is physically removed from the tree.
   */
  removeChild(child: T) {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const offset = this._children.indexOf(child);
    if (offset === -1) {
      // When a child has been detached by a prior merge operation,
      // removeChild during GC purge may not find it. This is safe to
      // skip since the child is already physically removed.
      return;
    }

    this._children.splice(offset, 1);
    // NOTE(hackerwins): Decrease totalSize including removed nodes
    // since this node is being purged (physically removed from tree).
    child.updateAncestorsSize(-child.paddedSize(true), true);
    child.parent = undefined;
  }

  /**
   * `detachChild` removes the given child from this node's children list
   * and updates both visibleSize and totalSize. Unlike `removeChild` which
   * is used for GC purge of tombstoned nodes, `detachChild` is used for
   * moving alive nodes between parents.
   */
  detachChild(child: T) {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const offset = this._children.indexOf(child);
    if (offset === -1) {
      throw new YorkieError(Code.ErrInvalidArgument, 'child not found');
    }

    this._children.splice(offset, 1);
    child.updateAncestorsSize(-child.paddedSize());
    child.updateAncestorsSize(-child.paddedSize(true), true);
    child.parent = undefined;
  }

  /**
   * `moveChild` detaches the given child from its current parent (if any)
   * and appends it to this node, preserving both size dimensions on both
   * parents. Unlike `detachChild` followed by `append`, it is correct for
   * tombstoned children: a removed node contributes no visibleSize to
   * either parent (removal already excluded it from its ancestors), so only
   * the include-removed totalSize is relocated. It is used by merge to move
   * tombstones as RGA anchors without corrupting index positions.
   */
  moveChild(child: T): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const removed = this.detachForMove(child);
    this._children.push(child);
    child.parent = this as any;
    this.attachAfterMove(child, removed);
  }

  /**
   * `moveChildBefore` detaches the given child from its current parent (if
   * any) and inserts it before `reference` among this node's children,
   * preserving both size dimensions on both parents. It is `moveChild` with a
   * position: see there for why a removed child relocates only its
   * include-removed size.
   *
   * Both failures are decided before anything moves, so a refused move leaves
   * the tree exactly as it was rather than holding a child that belongs to no
   * parent. The reference's offset is still read after the detach, since
   * removing the child can shift it when the two share a parent.
   */
  moveChildBefore(child: T, reference: T): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }
    // Already where it is being asked to go, and the detach below would make
    // the reference impossible to find again.
    if (child === reference) {
      return;
    }
    if (this._children.indexOf(reference) === -1) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'reference is not a child',
      );
    }

    const removed = this.detachForMove(child);
    this._children.splice(this._children.indexOf(reference), 0, child);
    child.parent = this as any;
    this.attachAfterMove(child, removed);
  }

  /**
   * `detachForMove` takes the child off its current parent, if it has one,
   * subtracting its sizes from that parent's ancestors, and reports whether
   * the child is a tombstone — which the re-attachment needs to know too.
   *
   * A tombstone moves only its include-removed size: it contributes no
   * visibleSize to either parent, because removal already took it out of its
   * ancestors. Splitting the move into this pair and `attachAfterMove` keeps
   * that rule in one place; stating it once per call site is how the two
   * dimensions drift apart.
   */
  private detachForMove(child: T): boolean {
    const removed = child.isRemoved;
    if (!child.parent) {
      return removed;
    }

    const offset = child.parent._children.indexOf(child);
    // The child may already have been spliced out of its parent's list by a
    // concurrent operation (e.g. cascade delete of a split sibling), which
    // already reconciled the old ancestors' size. In that case only
    // re-parent; otherwise detach with size accounting.
    if (offset !== -1) {
      child.parent._children.splice(offset, 1);
      if (!removed) {
        child.updateAncestorsSize(-child.paddedSize());
      }
      child.updateAncestorsSize(-child.paddedSize(true), true);
    }
    child.parent = undefined;

    return removed;
  }

  /**
   * `attachAfterMove` adds the child's sizes to its new parent's ancestors,
   * the mirror of `detachForMove`. `removed` is what that call reported.
   */
  private attachAfterMove(child: T, removed: boolean): void {
    if (!removed) {
      child.updateAncestorsSize(child.paddedSize());
    }
    child.updateAncestorsSize(child.paddedSize(true), true);
  }

  /**
   * `splitElement` splits the given element at the given offset.
   */
  splitElement(
    offset: number,
    issueTimeTicket: () => TimeTicket,
    versionVector?: VersionVector,
  ): [T | undefined, DataSize] {
    const diff = { data: 0, meta: 0 };

    const prvSize = this.getDataSize();

    /**
     * TODO(hackerwins): Define ID of split node for concurrent editing.
     * Text has fixed content and its split nodes could have limited offset
     * range. But element node could have arbitrary children and its split
     * nodes could have arbitrary offset range. So, id could be duplicated
     * and its order could be broken when concurrent editing happens.
     * Currently, we use the similar ID of split element with the split text.
     */
    const clone = this.cloneElement(issueTimeTicket);
    this.parent!.insertAfterInternal(clone, this as any);
    // A piece born tombstoned -- split off a node a concurrent deletion has
    // already removed -- is not visible, so it must not lengthen its live
    // ancestors. `remove` holds the same invariant from the other side,
    // subtracting a node's padded size from its ancestors as it becomes a
    // tombstone. The include-removed size counts tombstones and grows either
    // way.
    if (!clone.isRemoved) {
      clone.updateAncestorsSize(clone.paddedSize());
    }
    clone.updateAncestorsSize(clone.paddedSize(true), true);

    const left = this._children.slice(0, offset);
    const right = this._children.slice(offset);

    // Fix 8: Handle concurrent merge-moved children during split.
    // When a child was merge-moved from a source that is itself a child
    // of the node being split, the content was local to this level and
    // should stay in the original (left) node. When the source is
    // external (e.g., a sibling element that was merged), the content
    // should flow naturally to the split (right) node.
    const allChildren = [...left, ...right];
    const actualRight: Array<T> = [];
    for (const child of right) {
      if (
        'mergedFrom' in child &&
        (child as any).mergedFrom != null &&
        'mergedAt' in child &&
        (child as any).mergedAt != null
      ) {
        if (versionVector) {
          const mergedAt = (child as any).mergedAt as TimeTicket;
          if (!versionVector.afterOrEqual(mergedAt)) {
            // Check if the merge source is a child of this node.
            const mergedFrom = (child as any).mergedFrom;
            const sourceIsChild = allChildren.some(
              (sibling) =>
                'id' in sibling && (sibling as any).id.equals(mergedFrom),
            );
            if (sourceIsChild) {
              left.push(child);
              continue;
            }
          }
        }
      }
      actualRight.push(child);
    }

    // §7.3 Boundary Insert Migration: Move concurrent inserts at the
    // split boundary to the left. Element split siblings (with insPrevID)
    // are skipped — they are split products handled by §7.4.
    if (versionVector) {
      const movedToLeft: Array<T> = [];
      const remaining: Array<T> = [];
      let boundaryReached = false;
      for (const child of actualRight) {
        if (!boundaryReached) {
          // Skip element split siblings — they are split products,
          // not concurrent inserts. Text split siblings have
          // deterministic IDs and act as normal boundary markers.
          if (
            (child as any).insPrevID !== undefined &&
            !(child as any).isText
          ) {
            remaining.push(child);
            continue;
          }
          const actorID = (child as any).id.getCreatedAt().getActorID();
          const knownLamport = versionVector.get(actorID);
          if (
            knownLamport === undefined ||
            knownLamport < (child as any).id.getCreatedAt().getLamport()
          ) {
            movedToLeft.push(child);
            continue;
          }
        }
        boundaryReached = true;
        remaining.push(child);
      }
      if (movedToLeft.length > 0) {
        left.push(...movedToLeft);
        actualRight.length = 0;
        actualRight.push(...remaining);
      }
    }

    this._children = left;
    clone._children = actualRight;
    this.visibleSize = this._children.reduce(
      (acc, child) => acc + (child.isRemoved ? 0 : child.paddedSize()),
      0,
    );
    this.totalSize = this._children.reduce(
      (acc, child) => acc + child.paddedSize(true),
      0,
    );
    clone.visibleSize = clone._children.reduce(
      (acc, child) => acc + (child.isRemoved ? 0 : child.paddedSize()),
      0,
    );
    clone.totalSize = clone._children.reduce(
      (acc, child) => acc + child.paddedSize(true),
      0,
    );
    for (const child of clone._children) {
      child.parent = clone;
    }

    // NOTE(hackerwins): Calculate data size after node splitting:
    // Take the sum of the two split nodes(left and right) minus the size of
    // the original node. This calculates the net metadata overhead added by
    // the split operation.
    addDataSizes(diff, this.getDataSize(), clone.getDataSize());
    subDataSize(diff, prvSize);

    return [clone, diff];
  }

  /**
   * `insertAfterInternal` inserts the given node after the given child.
   * This method does not update the size of the ancestors.
   */
  insertAfterInternal(newNode: T, referenceNode: T): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    const offset = this._children.indexOf(referenceNode);
    if (offset === -1) {
      throw new YorkieError(Code.ErrInvalidArgument, 'child not found');
    }

    this.insertAtInternal(newNode, offset + 1);
  }

  /**
   * `insertAtInternal` inserts the given node at the given index.
   * This method does not update the size of the ancestors.
   */
  insertAtInternal(newNode: T, offset: number): void {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    this._children.splice(offset, 0, newNode);
    newNode.parent = this as any;
  }

  /**
   * findOffset returns the offset of the given node in the children.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  findOffset(node: T, includeRemoved: boolean = false): number {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    if (includeRemoved) {
      return this._children.indexOf(node);
    }

    if (node.isRemoved) {
      const index = this._children.indexOf(node);

      // If nodes are removed, the offset of the removed node is the number of
      // nodes before the node excluding the removed nodes.
      const refined = this.allChildren
        .splice(0, index)
        .filter((node) => !node.isRemoved).length;

      return refined;
    }

    return this.children.indexOf(node);
  }

  /**
   * `findBranchOffset` returns offset of the given descendant node in this node.
   * If the given node is not a descendant of this node, it returns -1.
   */
  findBranchOffset(node: T): number {
    if (this.isText) {
      throw new YorkieError(Code.ErrRefused, 'Text node cannot have children');
    }

    let current: T | undefined = node;
    while (current) {
      const offset = this._children.indexOf(current);
      if (offset !== -1) {
        return offset;
      }

      current = current.parent;
    }

    return -1;
  }
}

/**
 * `TreePos` is the position of a node in the tree.
 *
 * `offset` is the position of node's token. For example, if the node is an
 * element node, the offset is the index of the child node. If the node is a
 * text node, the offset is the index of the character.
 */
export type TreePos<T extends IndexTreeNode<T>> = {
  node: T;
  offset: number;
};

/**
 * `ancestorOf` returns true if the given node is an ancestor of the other node.
 */
function ancestorOf<T extends IndexTreeNode<T>>(ancestor: T, node: T): boolean {
  if (ancestor === node) {
    return false;
  }

  while (node.parent) {
    if (node.parent === ancestor) {
      return true;
    }
    node = node.parent;
  }
  return false;
}

/**
 * `TokenType` represents the type of token in XML representation.
 */
export enum TokenType {
  /**
   * `Start` represents that the start token type.
   */
  Start = 'Start',

  /**
   * `End` represents that the end token type.
   */
  End = 'End',

  /**
   * `Text` represents that the text token type.
   */
  Text = 'Text',
}

/**
 * `TreeToken` represents the token of the tree in XML representation.
 */
export type TreeToken<T> = [T, TokenType];

/**
 * `tokensBetween` iterates the tokens between the given range.
 *
 * For example, if the tree is <p><i>abc</i></p>, the tokens are
 * [p, Start], [i, Start], [abc, Text], [i, End], [p, End].
 *
 * If the given range is collapsed, the callback is not called.
 * It traverses the tree based on the concept of token.
 * NOTE(sejongk): Nodes should not be removed in callback, because it leads wrong behaviors.
 */
function tokensBetween<T extends IndexTreeNode<T>>(
  root: T,
  from: number,
  to: number,
  callback: (token: TreeToken<T>, ended: boolean) => void,
  includeRemoved: boolean = false,
) {
  if (from > to) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      `from is greater than to: ${from} > ${to}`,
    );
  }

  const rootSize = includeRemoved ? root.totalSize : root.visibleSize;
  if (from > rootSize) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      `from is out of range: ${from} > ${rootSize}`,
    );
  }
  if (to > rootSize) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      `to is out of range: ${to} > ${rootSize}`,
    );
  }

  if (from === to) {
    return;
  }

  let pos = 0;
  const children = includeRemoved ? root._children : root.children;
  for (const child of children) {
    // If the child is an element node, the size of the child.
    if (from - child.paddedSize(includeRemoved) < pos && pos < to) {
      // If the child is an element node, the range of the child
      // is from - 1 to to - 1. Because the range of the element node is from
      // the open tag to the close tag.
      const fromChild = child.isText ? from - pos : from - pos - 1;
      const toChild = child.isText ? to - pos : to - pos - 1;

      // If the range spans outside the child,
      // the callback is called with the child.
      const childSize = includeRemoved ? child.totalSize : child.visibleSize;
      const startContained = !child.isText && fromChild < 0;
      const endContained = !child.isText && toChild > childSize;
      if (child.isText || startContained) {
        callback(
          [child, child.isText ? TokenType.Text : TokenType.Start],
          endContained,
        );
      }
      tokensBetween(
        child,
        Math.max(0, fromChild),
        Math.min(toChild, childSize),
        callback,
        includeRemoved,
      );
      if (endContained) {
        callback([child, TokenType.End], endContained);
      }
    }
    pos += child.paddedSize(includeRemoved);
  }
}

/**
 * `traverse` traverses the tree with postorder traversal.
 */
export function traverse<T extends IndexTreeNode<T>>(
  node: T,
  callback: (node: T, depth: number) => void,
  depth = 0,
) {
  for (const child of node.children) {
    traverse(child, callback, depth + 1);
  }
  callback(node, depth);
}

/**
 * `traverseAll` traverses the whole tree (include tombstones) with postorder traversal.
 */
export function traverseAll<T extends IndexTreeNode<T>>(
  node: T,
  callback: (node: T, depth: number) => void,
  depth = 0,
) {
  for (const child of node._children) {
    traverseAll(child, callback, depth + 1);
  }
  callback(node, depth);
}

/**
 * `findTreePos` finds the position of the given index in the given node.
 */
function findTreePos<T extends IndexTreeNode<T>>(
  node: T,
  index: number,
  preferText = true,
): TreePos<T> {
  if (index > node.visibleSize) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      `index is out of range: ${index} > ${node.visibleSize}`,
    );
  }

  if (node.isText) {
    return { node, offset: index };
  }

  // offset is the index of the child node.
  // pos is the window of the index in the given node.
  let offset = 0;
  let pos = 0;
  for (const child of node.children) {
    // The pos is in bothsides of the text node, we should traverse
    // inside of the text node if preferText is true.
    if (preferText && child.isText && child.visibleSize >= index - pos) {
      return findTreePos(child, index - pos, preferText);
    }

    // The position is in leftside of the element node.
    if (index === pos) {
      return { node, offset };
    }

    // The position is in rightside of the element node and preferText is false.
    if (!preferText && child.paddedSize() === index - pos) {
      return { node, offset: offset + 1 };
    }

    // The position is in middle the element node.
    if (child.paddedSize() > index - pos) {
      // If we traverse inside of the element node, we should skip the open.
      const skipOpenSize = 1;
      return findTreePos(child, index - pos - skipOpenSize, preferText);
    }

    pos += child.paddedSize();
    offset += 1;
  }

  // The position is in rightmost of the given node.
  return { node, offset };
}

/**
 * `getAncestors` returns the ancestors of the given node.
 */
export function getAncestors<T extends IndexTreeNode<T>>(node: T): Array<T> {
  const ancestors: Array<T> = [];
  let parent = node.parent;
  while (parent) {
    ancestors.unshift(parent);
    parent = parent.parent;
  }
  return ancestors;
}

/**
 * `findCommonAncestor` finds the lowest common ancestor of the given nodes.
 */
export function findCommonAncestor<T extends IndexTreeNode<T>>(
  nodeA: T,
  nodeB: T,
): T | undefined {
  if (nodeA === nodeB) {
    return nodeA;
  }

  const ancestorsOfA = getAncestors(nodeA);
  const ancestorsOfB = getAncestors(nodeB);

  let commonAncestor: T | undefined;
  for (let i = 0; i < ancestorsOfA.length; i++) {
    const ancestorOfA = ancestorsOfA[i];
    const ancestorOfB = ancestorsOfB[i];

    if (ancestorOfA !== ancestorOfB) {
      break;
    }

    commonAncestor = ancestorOfA;
  }

  return commonAncestor;
}

/**
 * `findLeftmost` finds the leftmost node of the given tree.
 */
export function findLeftmost<T extends IndexTreeNode<T>>(node: T): T {
  if (node.isText || node.children.length === 0) {
    return node;
  }

  return findLeftmost(node.children[0]);
}

/**
 * `findTextPos` returns the tree position of the given path element.
 */
function findTextPos<T extends IndexTreeNode<T>>(node: T, pathElement: number) {
  if (node.visibleSize < pathElement) {
    throw new YorkieError(Code.ErrInvalidArgument, 'unacceptable path');
  }

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];

    if (child.visibleSize < pathElement) {
      pathElement -= child.visibleSize;
    } else {
      node = child;

      break;
    }
  }

  return { node, offset: pathElement };
}

/**
 * `IndexTree` is a tree structure for linear indexing.
 */
export class IndexTree<T extends IndexTreeNode<T>> {
  private root: T;

  constructor(root: T) {
    this.root = root;
  }

  /**
   * `tokensBetween` returns the tokens between the given range.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  tokensBetween(
    from: number,
    to: number,
    callback: (token: TreeToken<T>, ended: boolean) => void,
    includeRemoved: boolean = false,
  ): void {
    tokensBetween<T>(this.root, from, to, callback, includeRemoved);
  }

  /**
   * `traverse` traverses the tree with postorder traversal.
   */
  traverse(callback: (node: T) => void): void {
    traverse(this.root, callback, 0);
  }

  /**
   * `traverseAll` traverses the whole tree (include tombstones) with postorder traversal.
   */
  traverseAll(callback: (node: T) => void): void {
    traverseAll(this.root, callback, 0);
  }

  /**
   * findTreePos finds the position of the given index in the tree.
   */
  public findTreePos(index: number, preferText = true): TreePos<T> {
    return findTreePos(this.root, index, preferText);
  }

  /**
   * `treePosToPath` returns path from given treePos
   */
  public treePosToPath(treePos: TreePos<T>) {
    const path = [];
    let node = treePos.node;

    if (node.isText) {
      const offset = node.parent!.findOffset(node);
      if (offset === -1) {
        throw new YorkieError(Code.ErrInvalidArgument, 'invalid treePos');
      }

      const sizeOfLeftSiblings = addSizeOfLeftSiblings(
        node.parent! as T,
        offset,
      );
      path.push(sizeOfLeftSiblings + treePos.offset);
      node = node.parent!;
    } else if (node.hasTextChild()) {
      // TODO(hackerwins): The function does not consider the situation
      // where Element and Text nodes are mixed in the Element's Children.
      const sizeOfLeftSiblings = addSizeOfLeftSiblings(
        node! as T,
        treePos.offset,
      );
      path.push(sizeOfLeftSiblings);
    } else {
      path.push(treePos.offset);
    }

    while (node.parent) {
      const offset = node.parent.findOffset(node);
      if (offset === -1) {
        throw new YorkieError(Code.ErrInvalidArgument, 'invalid treePos');
      }

      path.push(offset);
      node = node.parent;
    }

    return path.reverse();
  }

  /**
   * `pathToIndex` returns index from given path
   */
  public pathToIndex(path: Array<number>): number {
    const treePos = this.pathToTreePos(path);

    return this.indexOf(treePos);
  }

  /**
   * `pathToTreePos` returns treePos from given path
   */
  public pathToTreePos(path: Array<number>): TreePos<T> {
    if (!path.length) {
      throw new YorkieError(Code.ErrInvalidArgument, 'unacceptable path');
    }

    let node = this.root;
    for (let i = 0; i < path.length - 1; i++) {
      const pathElement = path[i];
      node = node.children[pathElement];

      if (!node) {
        throw new YorkieError(Code.ErrInvalidArgument, 'unacceptable path');
      }
    }

    if (node.hasTextChild()) {
      return findTextPos(node, path[path.length - 1]);
    }

    if (node.children.length < path[path.length - 1]) {
      throw new YorkieError(Code.ErrInvalidArgument, 'unacceptable path');
    }

    return {
      node,
      offset: path[path.length - 1],
    };
  }

  /**
   * `getRoot` returns the root node of the tree.
   */
  public getRoot(): T {
    return this.root;
  }

  /**
   * `getSize` returns the size of the tree.
   */
  public get size(): number {
    return this.root.visibleSize;
  }

  /**
   * `findPostorderRight` finds right node of the given tree position with
   *  postorder traversal.
   */
  public findPostorderRight(treePos: TreePos<T>): T | undefined {
    const { node, offset } = treePos;

    if (node.isText) {
      if (node.visibleSize === offset) {
        const nextSibling = node.nextSibling;
        if (nextSibling) {
          return nextSibling;
        }

        return node.parent;
      }

      return node;
    }

    if (node.children.length === offset) {
      return node;
    }

    return findLeftmost(node.children[offset]);
  }

  /**
   * `indexOf` returns the index of the given tree position.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  public indexOf(pos: TreePos<T>, includeRemoved: boolean = false): number {
    let { node } = pos;
    const { offset } = pos;

    let size = 0;
    let depth = 1;
    if (node.isText) {
      size += offset;

      const parent = node.parent! as T;
      const offsetOfNode = parent.findOffset(node, includeRemoved);
      if (offsetOfNode === -1) {
        throw new YorkieError(Code.ErrInvalidArgument, 'invalid pos');
      }

      size += addSizeOfLeftSiblings(parent, offsetOfNode, includeRemoved);

      node = node.parent!;
    } else {
      size += addSizeOfLeftSiblings(node, offset, includeRemoved);
    }

    while (node?.parent) {
      const parent = node.parent;
      const offsetOfNode = parent.findOffset(node, includeRemoved);
      if (offsetOfNode === -1) {
        throw new YorkieError(Code.ErrInvalidArgument, 'invalid pos');
      }

      size += addSizeOfLeftSiblings(parent, offsetOfNode, includeRemoved);
      depth++;
      node = node.parent;
    }

    return size + depth - 1;
  }

  /**
   * `indexToPath` returns the path of the given index.
   */
  public indexToPath(index: number): Array<number> {
    const treePos = this.findTreePos(index);
    return this.treePosToPath(treePos);
  }
}
